import AuthContext from "context/AuthContext";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import useToastContext from "hooks/useToastContext";
import QuestionsView from "pages/test_manager/components/QuestionsView";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { apiGet } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import helpers from "utils/helpers";

dayjs.extend(duration);

function TestDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [test, setTest] = useState(null);
  const notification = useToastContext();

  const fetchTest = useCallback(async () => {
    try {
      const path = apiPath.quizTests + "/" + id;
      const { data } = await apiGet(path);
      setTest(data?.results);
    } catch (error) {
      console.log(error);
      notification.error(error?.response?.data?.message);
    }
  }, [id, notification]);

  useEffect(() => {
    fetchTest();
  }, [fetchTest]);

  return (
    <div className="bg-white px-3 py-4 ">
      <h1 className="text-2xl font-bold">Test Details</h1>
      <hr className="my-4" />

      <div className="flex flex-col gap-12">
        <section className="flex flex-col gap-3">
          <h2 className="text-xl font-bold">General Information</h2>
          <div className="flex flex-col gap-2 *:py-3 border rounded-md p-2 shadow-lg">
            <div className="flex-1 flex  gap-2 border-b">
              <div className="flex flex-col flex-1 gap-2">
                <p className="font-bold">Title: </p>
                <p>{test?.title}</p>
              </div>
              <div className="flex flex-col flex-1 gap-2">
                <p className="font-bold">Title (Arabic): </p>
                <p>{test?.titleAr}</p>
              </div>
            </div>

            <div className="flex  gap-2 border-b">
              <div className="flex flex-col flex-1 gap-2">
                <p className="font-bold">Description:</p>
                <p>{test?.description}</p>
              </div>
              <div className="flex flex-col flex-1 gap-2">
                <p className="font-bold">Description (Arabic):</p>
                <p>{test?.descriptionAr}</p>
              </div>
            </div>
            <div className="flex flex-row gap-2 border-b">
              <div className="flex-1 flex-col  gap-2">
                <p className="font-bold">Start Date:</p>
                <p>{helpers.dateFormat(test?.startTime)}</p>
              </div>

              <div className="flex-1 flex-col  gap-2">
                <p className="font-bold">End Date:</p>
                <p>{helpers.dateFormat(test?.endTime)}</p>
              </div>
            </div>

            <div className="flex gap-4 border-b">
              <div className="flex-1">
                <p className="font-bold">Attempts:</p>
                <p>{test?.attempts}</p>
              </div>
              <div className="flex-1">
                <p className="font-bold">Status:</p>
                <p
                  className={`${
                    test?.status === "active"
                      ? "text-green-500"
                      : "text-red-500"
                  }`}
                >
                  {test?.status === "active" ? "Active" : "Inactive"}
                </p>
              </div>
            </div>
            <div className="flex gap-4 border-b">
              <div className="flex-1">
                <p className="font-bold">Number of Questions:</p>
                <p>{test?.questions?.length}</p>
              </div>
              <div className="flex-1">
                <p className="font-bold">Test Type:</p>
                <p>{test?.testType === "quiz" ? "Quiz" : "Practice"}</p>
              </div>
            </div>
            <div className="flex gap-4 border-b">
              <div className="flex-1">
                <p className="font-bold">Number of Submissions:</p>
                <p>{test?.submissions?.length}</p>
              </div>
              <div className="flex-1">
                <p className="font-bold">Last Update:</p>
                <p>{helpers.dateFormat(test?.updatedAt)}</p>
              </div>
            </div>
            <div className="flex gap-4 border-b">
              <div className="flex-1">
                <p className="font-bold">Time Until Start:</p>
                <p>{helpers.remainingTimeFromNow(test?.startTime)}</p>
              </div>
              <div className="flex-1">
                <p className="font-bold">Test Duration:</p>
                <p>
                  {test?.startTime && test?.endTime
                    ? (() => {
                        const diff = dayjs(test?.endTime).diff(
                          dayjs(test?.startTime)
                        );
                        const dur = dayjs.duration(diff);
                        const days = Math.floor(dur.asDays());
                        const hours = dur.hours();
                        const minutes = dur.minutes();

                        const parts = [];
                        if (days > 0)
                          parts.push(`${days} day${days > 1 ? "s" : ""}`);
                        if (hours > 0)
                          parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
                        if (minutes > 0)
                          parts.push(
                            `${minutes} minute${minutes > 1 ? "s" : ""}`
                          );

                        return parts.length > 0 ? parts.join(" ") : "0 minutes";
                      })()
                    : "-"}
                </p>
              </div>
              <div className="flex-1">
                <p className="font-bold">Timer:</p>
                <p>{test?.showTimer ? "Enabled" : "Disabled"}</p>
              </div>
            </div>
            <div className="flex gap-4 ">
              <div className="flex-1">
                <p className="font-bold">Passing Percentage:</p>
                <p>
                  {test?.passingPercentage
                    ? test?.passingPercentage + "%"
                    : "0%"}
                </p>
              </div>
            </div>
          </div>
        </section>
        <hr />
        <QuestionsView
          questions={test?.questions}
          onQuestionsChanged={fetchTest}
        />
      </div>
    </div>
  );
}

export default TestDetailsPage;
